import { useState, useCallback } from "react";
import { ScrollView, Platform } from "react-native";
import { PressableScale } from "pressto";
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import { ScreenContainer, FormInput, Button } from "@src/components";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { showErrorToast, showInfoToast } from "@src/utils/toast";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { router } from "expo-router";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { legacyApi } from "@src/services/api/legacy-client";
import { ValidationError, NetworkError } from "@src/services/api/legacy-api-request";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { useAuthStore } from "@src/features/auth";
import { LoadingScreen } from "@src/features/auth/components/LoadingScreen";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { usePendingEnvironmentSwitch } from "@src/config/usePendingEnvironmentSwitch";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { Icon } from "@src/components/Icon";
import { ContactUsButton } from "@src/components/ContactUsButton";
import { EnvironmentResetBadge } from "@src/components/EnvironmentResetBadge";
import LogoIcon from "@src/assets/svgs/logo-icon.svg";
import LogoType from "@src/assets/svgs/logo-type.svg";

const KEYBOARD_HEIGHT = -380;
const KEYBOARD_OFFSET = KEYBOARD_HEIGHT / 2;

export default function LoginScreen() {
  const { theme } = useUnistyles();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isSwitching: isSwitchingEnv } = usePendingEnvironmentSwitch();
  const { progress } = useReanimatedKeyboardAnimation();
  const { initialize } = useAuthStore();
  const { track } = useAmplitude();

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: progress.value * KEYBOARD_OFFSET }],
    flex: progress.value > 0 ? 0 : 1,
  }));

  const animatedMarginTopStyle = useAnimatedStyle(
    () => ({
      marginTop: progress.value > 0 ? 0 : theme.spacing["14xl"],
    }),
    [theme.spacing["14xl"]],
  );

  const handleLogin = useCallback(async () => {
    if (!username.trim() || !password.trim()) {
      showInfoToast({ message: "Please enter your username and password" });
      return;
    }

    setIsLoading(true);
    track(ANALYTICS_EVENTS.LEGACY_LOGIN_STARTED);

    try {
      // Authenticate with Django API
      const response = await legacyApi.login.authenticate(username.trim(), password);

      // Store tokens in secure storage for legacy code access
      await secureStorage.set(SecureStorageKeys.legacyUserId, response.user_id);
      await secureStorage.set(SecureStorageKeys.legacyUserEmail, username.trim());
      await secureStorage.set(SecureStorageKeys.legacyAuthToken, response.token);

      // Initialize auth store from the newly stored legacy credentials
      // This will set legacyAuth, user, tenants, and analytics state.
      await initialize();

      track(ANALYTICS_EVENTS.LEGACY_LOGIN_COMPLETED, {
        user_id: response.user_id,
      });

      // Web: skip native-only biometrics/PIN onboarding, go straight to overview
      // Native: proceed to biometrics setup as before
      router.replace(Platform.OS === "web" ? "/overview" : "/auth/biometrics");
    } catch (err) {
      track(ANALYTICS_EVENTS.LEGACY_LOGIN_FAILED, {
        error_type:
          err instanceof ValidationError ? "validation"
          : err instanceof NetworkError ? "network"
          : "unknown",
      });

      if (err instanceof ValidationError) {
        showErrorToast({ message: "Invalid username or password" });
      } else {
        showErrorToast({ error: err });
      }
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [username, password, initialize, track]);

  const onPasswordVisibilityChange = useCallback(() => {
    setIsPasswordVisible((isPasswordVisible) => !isPasswordVisible);
  }, []);

  const handleForgotPassword = useCallback(() => {
    track(ANALYTICS_EVENTS.FORGOT_PASSWORD_CLICKED);
    router.push("/auth/forgot-password");
  }, [track]);

  if (isSwitchingEnv) {
    return <LoadingScreen message="Switching environment…" />;
  }

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer edges={["top", "bottom"]} style={{ backgroundColor: "transparent" }}>
          <ScrollView
            contentContainerStyle={{ flexGrow: 1, paddingHorizontal: theme.spacing["5xl"], justifyContent: "center" }}
          >
            <Animated.View style={[animatedStyle]}>
              <View justifyContent="center" alignItems="center">
                <View flexDirection="row" alignItems="center" mt="8xl" mb="2xl">
                  <LogoIcon width={75} height={70} />
                  <LogoType width={195} height={70} color={theme.colors.interactive.buttons.default_foreground} />
                </View>
              </View>

              <EnvironmentResetBadge />

              <Animated.View style={animatedMarginTopStyle}>
                <View mb="4xl">
                  <FormInput
                    variant="outline"
                    value={username}
                    onChangeText={setUsername}
                    placeholder="Email"
                    autoCapitalize="none"
                    keyboardType="email-address"
                    returnKeyType="next"
                    size="large"
                    editable={!isLoading}
                  />
                </View>

                <View mb="6xl">
                  <FormInput
                    variant="outline"
                    value={password}
                    onChangeText={setPassword}
                    placeholder="Password"
                    secureTextEntry={!isPasswordVisible}
                    autoCapitalize="none"
                    returnKeyType="done"
                    onSubmitEditing={handleLogin}
                    size="large"
                    editable={!isLoading}
                    iconRight={
                      <PressableScale onPress={onPasswordVisibilityChange}>
                        <Icon
                          name={isPasswordVisible ? "visibilityOff" : "visibility"}
                          size={20}
                          color={theme.colors.white.default}
                        />
                      </PressableScale>
                    }
                  />
                </View>

                <View mb="4xl">
                  <Button
                    onPress={handleLogin}
                    label="Log in"
                    size="md"
                    textStyle="md/regular"
                    variant="primary"
                    textColor={theme.colors.interactive.buttons.default_foreground}
                    loading={isLoading}
                    loadingText="Logging in..."
                    disabled={isLoading}
                  />
                </View>
                <View mb="4xl">
                  <Button
                    onPress={handleForgotPassword}
                    label="Forgot Password?"
                    size="md"
                    variant="link"
                    textStyle="md/regular"
                    textColor={theme.colors.interactive.buttons.default_foreground}
                    disabled={isLoading}
                  />
                </View>
              </Animated.View>
            </Animated.View>
            <View flexDirection="row" justifyContent="center" alignItems="center" mb="xl" gap="xs">
              <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
                Having a problem?
              </Text>
              <ContactUsButton />
            </View>
          </ScrollView>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));
